import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, MessageSquarePlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useReviewStore } from '../store/reviewStore';
import { useAuthStore } from '../store/authStore';
import type { ReviewAnswer, ReviewQuestion } from '../types/review';

/**
 * A stakeholder review page: report content plus answer cards. Unlisted on
 * purpose — reviewers arrive by direct link. Answers are attributed to the
 * logged-in user server-side, and everyone's answers render under each
 * question so reviewers see where the group stands.
 */

function answerName(a: ReviewAnswer): string {
  return a.user.displayName || a.user.email;
}

function QuestionCard({
  slug,
  question,
  answers,
  myAnswer,
}: {
  slug: string;
  question: ReviewQuestion;
  answers: ReviewAnswer[];
  myAnswer: ReviewAnswer | undefined;
}) {
  const saveAnswer = useReviewStore((s) => s.saveAnswer);
  // Start from the reviewer's saved answer: the server replaces both fields on
  // every save, so blank controls would silently clear a returning reviewer's
  // earlier choice or comment.
  const [choice, setChoice] = useState<string>(() => myAnswer?.choice ?? '');
  const [comment, setComment] = useState(() => myAnswer?.comment ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const handleSave = async () => {
    if (!choice && !comment.trim()) {
      toast.error('Pick an option or write a comment first');
      return;
    }
    setIsSaving(true);
    try {
      const body: { choice?: string; comment?: string } = {};
      if (choice) body.choice = choice;
      if (comment.trim()) body.comment = comment.trim();
      await saveAnswer(slug, question.key, body);
      setSavedAt(Date.now());
    } catch {
      toast.error('Could not save your answer — try again');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{question.title}</h3>
      {question.context && (
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{question.context}</p>
      )}

      <div className="mt-3 space-y-2" role="radiogroup" aria-label={question.title}>
        {question.options.map((option) => (
          <label
            key={option}
            className={`block cursor-pointer rounded-md border px-3 py-2 text-sm ${
              choice === option
                ? 'border-blast-500 bg-blast-50 text-gray-900 dark:bg-blast-950 dark:text-gray-100'
                : 'border-gray-200 text-gray-800 dark:border-gray-700 dark:text-gray-200'
            }`}
          >
            <input
              type="radio"
              name={question.key}
              value={option}
              checked={choice === option}
              onChange={() => setChoice(option)}
              className="mr-2 accent-blast-600"
              aria-label={option}
            />
            {option}
          </label>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional comment…"
        aria-label={`Comment on ${question.title}`}
        className="input mt-3 min-h-[64px] w-full"
      />

      <div className="mt-2 flex items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save answer'}
        </button>
        {savedAt && !isSaving && (
          <span className="flex items-center gap-1 text-sm font-medium text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" /> Saved
          </span>
        )}
      </div>

      <div className="mt-4 border-t border-dashed border-gray-200 pt-3 dark:border-gray-700">
        {answers.length === 0 ? (
          <p className="text-sm italic text-gray-500 dark:text-gray-400">No answers yet.</p>
        ) : (
          <ul className="space-y-2">
            {answers.map((a) => (
              <li key={a.id} className="text-sm text-gray-800 dark:text-gray-200">
                <span className="mr-2 rounded bg-blast-50 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-blast-700 dark:bg-blast-950 dark:text-blast-300">
                  {answerName(a)}
                </span>
                <span className="font-medium">{a.choice ?? '(comment only)'}</span>
                {a.comment && (
                  <span className="text-gray-600 dark:text-gray-400"> — “{a.comment}”</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ReviewPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { user: currentUser } = useAuthStore();
  const { review, isLoading, error, fetch, addNote } = useReviewStore();
  const [noteText, setNoteText] = useState('');
  const [isPosting, setIsPosting] = useState(false);

  useEffect(() => {
    if (slug) void fetch(slug);
  }, [slug, fetch]);

  const handlePostNote = async () => {
    if (!noteText.trim()) return;
    setIsPosting(true);
    try {
      await addNote(slug, noteText.trim());
      setNoteText('');
    } catch {
      toast.error('Could not post your note — try again');
    } finally {
      setIsPosting(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Loading…</div>;
  }
  if (error || !review) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center dark:border-gray-700 dark:bg-gray-800">
          <p className="font-medium text-gray-900 dark:text-gray-100">{error ?? 'Review not found'}</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Check the link you were sent — review pages are only reachable by their exact address.
          </p>
        </div>
      </div>
    );
  }

  const answersByQuestion = new Map<string, ReviewAnswer[]>();
  for (const a of review.answers) {
    const list = answersByQuestion.get(a.questionKey) ?? [];
    list.push(a);
    answersByQuestion.set(a.questionKey, list);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{review.title}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Read through, answer the questions below, and add anything else as a note at the bottom.
          Your answers save under your own name.
        </p>
      </header>

      {review.content.sections.map((section, i) => (
        <article
          key={i}
          className="review-doc rounded-lg border border-gray-200 bg-white p-4 text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 sm:p-5"
          // Review content is authored exclusively by admins through a
          // validated, admin-gated endpoint — it is trusted markup.
          dangerouslySetInnerHTML={{ __html: section.html }}
        />
      ))}

      <section aria-label="Questions" className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your answers</h2>
        {review.content.questions.map((q) => {
          const questionAnswers = answersByQuestion.get(q.key) ?? [];
          return (
            <QuestionCard
              key={`${review.id}:${q.key}`}
              slug={slug}
              question={q}
              answers={questionAnswers}
              myAnswer={questionAnswers.find((a) => a.user.id === currentUser?.id)}
            />
          );
        })}
      </section>

      <section aria-label="Notes" className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Anything else?</h2>
        {review.notes.length > 0 && (
          <ul className="space-y-2">
            {review.notes.map((n) => (
              <li
                key={n.id}
                className="rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800"
              >
                <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-semibold uppercase tracking-wide text-blast-700 dark:text-blast-300">
                    {n.user.displayName || n.user.email}
                  </span>{' '}
                  · {new Date(n.createdAt).toLocaleString()}
                </div>
                <div className="text-gray-800 dark:text-gray-200">{n.text}</div>
              </li>
            ))}
          </ul>
        )}
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Write a note…"
          aria-label="Add a note"
          className="input min-h-[64px] w-full"
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handlePostNote}
          disabled={isPosting || !noteText.trim()}
        >
          <MessageSquarePlus className="mr-1 inline h-4 w-4" />
          {isPosting ? 'Posting…' : 'Post note'}
        </button>
      </section>
    </div>
  );
}
